import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, X, Check } from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentProfileSafe, getCurrentUserId, supabase, Flat } from '../lib/supabase';
import AuditService from '../services/AuditService';
import toast from 'react-hot-toast';
import { formatDate } from '../lib/dateUtils';

const Flats: React.FC = () => {
  const [flats, setFlats] = useState<Flat[]>([]);
  const [loading, setLoading] = useState(true);
  const [flatRole, setFlatRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFlat, setEditingFlat] = useState<Flat | null>(null);
  const [formData, setFormData] = useState({
    flat_number: '',
    physical_address: '',
    participation_quota: '',
    status: 'occupied' as 'occupied' | 'vacant' | 'inactive',
  });

  const [bodyCorporateId, setBodyCorporateId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const profile = await getCurrentProfileSafe();
      setFlatRole(profile?.role || 'staff');

      const id = await getCurrentBodyCorporateId();
      setBodyCorporateId(id);
      await fetchFlats(id);
    };

    load();
  }, []);

  const fetchFlats = async (id: string | null) => {
    setLoading(true);

    if (!id) {
      setFlats([]);
      setLoading(false);
      toast.error('No body corporate is configured for this user.');
      return;
    }

    const { data, error } = await supabase
      .from('flats')
      .select('*')
      .eq('body_corporate_id', id)
      .order('flat_number');

    if (error) {
      toast.error('Error fetching flats');
      console.error(error);
    } else {
      setFlats(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (flatRole === 'viewer') {
      toast.error('Viewer accounts can only view flats.');
      return;
    }

    if (editingFlat) {
      const changes = {
        flat_number: formData.flat_number,
        physical_address: formData.physical_address,
        participation_quota: formData.participation_quota === '' ? null : Number(formData.participation_quota),
        status: formData.status,
      };
      // Update
      const { error } = await supabase
        .from('flats')
        .update(changes).eq('id', editingFlat.id).select().single();

      if (error) {
        toast.error('Error updating flat');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('UPDATE', 'flat', editingFlat.id, editingFlat, { ...editingFlat, ...changes });
        toast.success('Flat updated successfully');
        fetchFlats(bodyCorporateId);
        closeModal();
      }
    } else {
      // Create
      const { data: createdFlat, error } = await supabase
        .from('flats')
        .insert({
          body_corporate_id: bodyCorporateId,
          flat_number: formData.flat_number,
          physical_address: formData.physical_address,
          participation_quota: formData.participation_quota === '' ? null : Number(formData.participation_quota),
          status: formData.status,
        }).select().single();

      if (error) {
        toast.error('Error creating flat');
        console.error(error);
      } else {
        const userId = await getCurrentUserId();
        if (bodyCorporateId && userId && createdFlat) await new AuditService(bodyCorporateId, userId).logAction('CREATE', 'flat', createdFlat.id, undefined, createdFlat);
        toast.success('Flat created successfully');
        fetchFlats(bodyCorporateId);
        closeModal();
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this flat?')) return;

    const deletedFlat = flats.find((flat) => flat.id === id);
    const { error } = await supabase
      .from('flats')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Error deleting flat');
      console.error(error);
    } else {
      const userId = await getCurrentUserId();
      if (bodyCorporateId && userId) await new AuditService(bodyCorporateId, userId).logAction('DELETE', 'flat', id, deletedFlat);
      toast.success('Flat deleted successfully');
      fetchFlats(bodyCorporateId);
    }
  };

  const openModal = (flat?: Flat) => {
    if (flat) {
      setEditingFlat(flat);
      setFormData({
        flat_number: flat.flat_number,
        physical_address: flat.physical_address || '',
        participation_quota: flat.participation_quota?.toString() || '',
        status: flat.status,
      });
    } else {
      setEditingFlat(null);
      setFormData({
        flat_number: '',
        physical_address: '',
        participation_quota: '',
        status: 'occupied',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFlat(null);
  };

  const filteredFlats = flats.filter(flat =>
    flat.flat_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    flat.physical_address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'occupied': return 'bg-green-100 text-green-800';
      case 'vacant': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Flats</h1>
          <p className="text-gray-500">Manage all flats/units in your scheme</p>
        </div>
        {flatRole !== 'viewer' && (
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 btn-primary"
          >
            <Plus size={20} />
            Add Flat
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by flat number or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filteredFlats.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No flats found. Click "Add Flat" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flat #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participation Quota</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredFlats.map((flat) => (
                  <tr key={flat.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{flat.flat_number}</td>
                    <td className="px-4 py-3 text-gray-600">{flat.physical_address || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {flat.participation_quota == null ? '-' : `${flat.participation_quota}%`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(flat.status)}`}>
                        {flat.status.charAt(0).toUpperCase() + flat.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatDate(flat.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {flatRole !== 'viewer' && (
                        <>
                          <button
                            onClick={() => openModal(flat)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(flat.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {editingFlat ? 'Edit Flat' : 'Add New Flat'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flat Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.flat_number}
                    onChange={(e) => setFormData({ ...formData, flat_number: e.target.value })}
                    placeholder="e.g., G1, A2, 101"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Physical Address
                  </label>
                  <input
                    type="text"
                    value={formData.physical_address}
                    onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                    placeholder="Unit address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Participation Quota (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.000001"
                    value={formData.participation_quota}
                    onChange={(e) => setFormData({ ...formData, participation_quota: e.target.value })}
                    placeholder="e.g., 2.500000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="occupied">Occupied</option>
                    <option value="vacant">Vacant</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                >
                  <Check size={20} />
                  {editingFlat ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Flats;
